import React from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import ThemeModeSlider from '../components/ThemeModeSlider'

const publicForms = [
  {
    title: 'Timesheet',
    description: 'Submit daily attendance and work hours',
    icon: '📋',
    path: '/timesheet-form',
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    bgDark: 'bg-blue-500/15',
    borderLight: 'border-blue-200',
    borderDark: 'border-blue-500/20',
  },
  {
    title: 'Receipt',
    description: 'Upload receipt expense claims',
    icon: '🧾',
    path: '/expense-form-receipt',
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50',
    bgDark: 'bg-emerald-500/15',
    borderLight: 'border-emerald-200',
    borderDark: 'border-emerald-500/20',
  },
  {
    title: 'Advanced',
    description: 'Submit advance payment requests',
    icon: '💸',
    path: '/expense-form-advanced',
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50',
    bgDark: 'bg-purple-500/15',
    borderLight: 'border-purple-200',
    borderDark: 'border-purple-500/20',
  },
]

export default function Welcome() {
  const { isDark, setTheme, themeOptions } = useTheme()

  const pageClass = isDark
    ? 'min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.22),_transparent_32%),linear-gradient(135deg,_#081120_0%,_#0f172a_42%,_#172554_100%)] text-white'
    : 'min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(96,165,250,0.16),_transparent_32%),linear-gradient(135deg,_#f8fafc_0%,_#e2e8f0_52%,_#dbeafe_100%)] text-slate-900'

  const glassClass = isDark
    ? 'border-white/15 bg-white/10 shadow-[0_28px_80px_rgba(15,23,42,0.45)] backdrop-blur-xl'
    : 'border-slate-200 bg-white/75 shadow-[0_28px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl'

  const panelClass = isDark
    ? 'bg-slate-950/80 ring-1 ring-white/10'
    : 'bg-white/90 ring-1 ring-slate-200/80'

  return (
    <div className={pageClass}>
      <div className={`absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:48px_48px] ${isDark ? 'opacity-30' : 'opacity-20'}`} />
      <div className="absolute right-6 top-6 z-20">
        <ThemeModeSlider
          isDark={isDark}
          onToggle={() => setTheme(isDark ? themeOptions.LIGHT : themeOptions.DARK)}
        />
      </div>
      <div className="relative mx-auto flex min-h-screen max-w-7xl items-center px-6 py-16 lg:px-12">
        <div className="grid w-full gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div className="max-w-2xl">
            <div className={`inline-flex items-center rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] shadow-lg backdrop-blur ${isDark ? 'border-white/15 bg-white/10 text-sky-100' : 'border-slate-300 bg-white text-sky-700'}`}>
              PROBARA GIGIH SDN BHD
            </div>
            <h1 className={`mt-6 text-5xl font-black leading-tight sm:text-6xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Operation Management System
            </h1>
            <p className={`mt-6 max-w-xl text-lg leading-8 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
              Track projects, invoices, timesheets, payroll, quotations, and expenses from one place.
              Start by signing in to access the dashboard.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/login"
                className={`inline-flex items-center justify-center rounded-full px-12 py-3 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5 ${
                  isDark
                    ? 'bg-white text-slate-900 shadow-[0_16px_40px_rgba(255,255,255,0.18)] hover:bg-slate-100'
                    : 'bg-slate-900 text-white shadow-[0_16px_40px_rgba(15,23,42,0.14)] hover:bg-slate-800'
                }`}
              >
                Login 
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className={`absolute -inset-6 rounded-[2rem] blur-3xl ${isDark ? 'bg-sky-500/20' : 'bg-sky-400/15'}`} />
            <div className={`relative rounded-[2rem] border p-6 ${glassClass}`}>
              <div className={`rounded-3xl p-6 ${panelClass}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs uppercase tracking-[0.3em] ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>Welcome</p>
                    <h2 className={`mt-2 text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Ready for the day</h2>
                  </div>
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${isDark ? 'bg-sky-500/20' : 'bg-sky-100'}`}>
                    ✨
                  </div>
                </div>

                <div id="features" className="mt-8 space-y-3">
                  <p className={`text-xs uppercase tracking-[0.25em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Quick Forms</p>
                  {publicForms.map((form) => (
                    <Link
                      key={form.path}
                      to={form.path}
                      className={`flex items-center gap-4 rounded-2xl border p-4 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg ${
                        isDark
                          ? `${form.borderDark} ${form.bgDark} hover:border-white/20`
                          : `${form.borderLight} ${form.bgLight} hover:border-slate-300`
                      }`}
                    >
                      <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${form.color} text-white text-xl shadow-md`}>
                        {form.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{form.title}</p>
                        <p className={`mt-0.5 text-sm leading-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{form.description}</p>
                      </div>
                      <span className={`flex-shrink-0 text-lg ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>→</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}